import type { AxiosResponse } from 'axios';
import { API_METHODS } from './api.enums';
import { apiAction } from './utils.actions';
import newAPIService, { getCancelSource } from './newAPIService';
import { ParsedCvJson } from '../../dal/entities/parsedCv';
import { AppliedFilter } from '../../types/aiCandidateSearch.types';
import { EmbeddingSearchModel } from '../../types/embeddingSearch.types';
import {
    AdminAiMatchRejectionFilter,
    AdminJobCvRejectionReason,
} from '../../common/admin_job_cv_rejection_reason.enum';

export const CANDIDATES_SEARCH_PAGE_SIZE = 40;

export type SearchCandidatesBody = {
    jobDescription: string
    latitude?: number
    longitude?: number
    distance?: number
    onlyShowToEmployers?: boolean
    jobId?: number
    appliedFilter?: AppliedFilter
    page?: number
    pageSize?: number
    embeddingModel?: EmbeddingSearchModel
}

export type CandidateRecord = {
    userId: number,
    cvId: number,
    cvSummary?: string,
    firstName: string,
    similarity: number,
    rankingScore?: number,
    locations?: string[],
    lastActivity?: Date,
    showToEmployers: boolean,
    location?: string,
    parsedCv?: ParsedCvJson | null,
    alreadyApplied?: boolean,
    adminRejection?: {
        reasonCode: AdminJobCvRejectionReason,
        createdAt: string,
    },
}

export type AdminSearchCandidatesBody = SearchCandidatesBody & {
    offset?: number
    rejectionFilter?: AdminAiMatchRejectionFilter
}

type CandidateSearchResponse = { candidates: CandidateRecord[]; hasMore: boolean };

async function requestCandidateSearch(
    url: string, payload: AdminSearchCandidatesBody, backdrop: boolean, signal?: AbortSignal,
): Promise<AxiosResponse<CandidateSearchResponse>> {
    // This application still uses Axios 0.21, which requires CancelToken.
    const source = getCancelSource();
    const abort = () => source.cancel();
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) abort();
    if (backdrop) window.showBackdrop();
    try {
        return await newAPIService.post<CandidateSearchResponse>(url, payload, {
            cancelToken: source.token,
            // A search may contain many database calls. Retrying 503/504 amplifies load.
            'axios-retry': { retries: 0 },
        });
    } finally {
        signal?.removeEventListener('abort', abort);
        if (backdrop) window.hideBackdrop();
    }
}

export const searchCandidatesAI = (
    payload: SearchCandidatesBody, backdrop?: boolean, signal?: AbortSignal,
) => requestCandidateSearch('/api/candidates/search', payload, backdrop ?? true, signal);

export const searchAdminCandidatesAI = (
    payload: AdminSearchCandidatesBody, backdrop?: boolean, signal?: AbortSignal,
) => requestCandidateSearch('/api/admin/candidates/search', payload, backdrop ?? true, signal);

export const sendPromotedJobToCandidate = (
    payload: { userId: number, jobId: number }, backdrop = true,
) => apiAction<{ success: boolean }>({
    url: '/api/candidates/send-promoted-job',
    method: API_METHODS.POST,
    payload,
    backdrop,
});
